/**
 * components/ItemFormModal.js
 * Create / edit a spare-part item. Both stock buckets are editable here on create;
 * after creation, day-to-day balance changes go through Penyesuaian / Transformasi
 * (so they are logged in riwayat). internalCode is the PK and is read-only on edit.
 */
import React, { useMemo, useState } from "react";
import CategoryPicker from "./CategoryPicker";
import { parseFitment } from "../utils/fitment";
import { parseMarketCodes } from "../utils/marketCodes";
import { useModalEsc } from "../utils/modalStack";
import FitmentEditor from "./FitmentEditor";
import MarketCodeEditor from "./MarketCodeEditor";
import SiblingEditor from "./SiblingEditor";
import ContactPicker from "./ContactPicker";

const blank = {
  internalCode: "",
  marketCodes: [],
  name: "",
  fitment: "",
  category: "",
  tipe: null,
  stokPusat: 0,
  stokCabang: 0,
  isSet: false,
  notes: "",
  minStock: null,
  siblingCodes: [],
};

export default function ItemFormModal({ initial, suggestedCode, createIsSet = false, recipes = [], categories = [], cars = [], items = [], contacts = [], initialSiblings = [], onSave, onClose, onCreateCar, onCreateContact, onAdjust }) {
  // Internal contacts are app-maintained TRANSFER stand-ins, not real shops — never offer one
  // as a pickable opening-stock source (Contact model redesign leak fix). A deactivated contact
  // is excluded the same way (Contact model redesign, stage 2) — this field is create-mode-only
  // and never preloaded from a persisted value, so there's no "already selected" case to
  // preserve here, unlike TransactionFormModal's own pickableContacts.
  // Deliberately NOT list-scoped (KontakPage batch 2b — TransactionFormModal/PenyesuaianModal
  // are, see their own comments): this single "Asal Stok" picker sits next to BOTH Stok Pusat
  // and Stok Cabang, editable together on the SAME create form, and the one
  // contact chosen here is stamped as the `contactId` on BOTH buckets' opening `PEMBUATAN`
  // history entries (`utils/history.js`'s `creationEntries`). There is no single location to
  // scope by — filtering to either list would silently misattribute the other bucket's opening
  // stock to a contact the user never chose for it. Confirmed as a deliberate product decision,
  // not an oversight: the honest fix, if this ever needs to be exact, is two separate
  // bucket-scoped pickers, which means carrying two contactIds through the create payload and
  // `creationEntries` instead of one — a bigger change than list-scoping, out of this batch.
  const pickableContacts = useMemo(() => contacts.filter((c) => !c.internal && !c.deactivated), [contacts]);
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(() => ({
    ...blank,
    internalCode: suggestedCode || "",
    ...(initial || {}),
    fitment: parseFitment(initial?.fitment),
    marketCodes: parseMarketCodes(initial?.marketCodes ?? initial?.marketCode),
    // Siblings live in data.siblingLinks, not on the item — always hydrated from the prop.
    siblingCodes: [...initialSiblings],
    isSet: isEdit ? (initial?.isSet === true) : (createIsSet === true),
  }));
  const [typeError, setTypeError] = useState("");
  const [pendingCategory, setPendingCategory] = useState(null); // { name, code } | null
  // CREATE mode only — the contact opening stock came from (e.g. a supplier). Kept out of
  // `form`/`blank` entirely (not an item field) so it's structurally impossible to leak into
  // an edit-mode payload; see submit()'s conditional spread below.
  const [openingContactId, setOpeningContactId] = useState(null);

  useModalEsc(onClose);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const usedInRecipe = useMemo(
    () => recipes.some((r) =>
      [...(r.inputs || []), ...(r.outputs || [])].some((l) => l.internalCode === form.internalCode)
    ),
    [recipes, form.internalCode]
  );

  const setType = (nextIsSet) => {
    if (nextIsSet) { setTypeError(""); set("isSet", true); return; }
    if (form.isSet && usedInRecipe) {
      setTypeError("SET ini dipakai di satu atau lebih resep. Hapus/ubah resep tersebut dulu sebelum mengubahnya menjadi spare part.");
      return;
    }
    setTypeError(""); set("isSet", false);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    // Defense in depth (Enter key submits): a create-mode item must have a category.
    // resolveItemCreate itself now also rejects a blank category (returns { error }, mints
    // nothing) — this guard is the friendly front line, not the only one: without it, the save
    // would still be blocked, just via saveItem's silent console-warned abort path instead of
    // this form's visible "Wajib diisi." hint.
    if (!isEdit && !form.category.trim()) return;
    onSave({
      ...form,
      name: form.name.trim(),
      marketCodes: parseMarketCodes(form.marketCodes),
      fitment: parseFitment(form.fitment),
      category: isEdit ? initial.category : form.category,
      stokPusat: isEdit ? initial.stokPusat : (Number(form.stokPusat) || 0),
      stokCabang: isEdit ? initial.stokCabang : (Number(form.stokCabang) || 0),
      minStock: form.minStock === "" || form.minStock == null ? null : Math.max(0, Number(form.minStock) || 0),
      // Transport-only: the user's explicit code for a new category. saveItem uses it for
      // resolveItemCreate and strips it before persisting the item.
      newCategoryCode: (!isEdit && pendingCategory && pendingCategory.name === form.category)
        ? pendingCategory.code
        : undefined,
      // Transport-only: staged sibling codes for saveItem to reconcile into data.siblingLinks;
      // not a field on the item itself (siblings live in the sibling-links collection).
      siblingCodes: form.siblingCodes,
      // Transport-only, CREATE only: the contact the opening stock came from. saveItem forwards
      // it into creationEntries and strips it before persisting the item. Absent entirely (not
      // just null) on an edit-mode submit — the field doesn't exist in this app's data model.
      ...(isEdit ? {} : { openingContactId: openingContactId || null }),
    });
  };

  const noun = form.isSet ? "SET" : "Barang";

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <form className="modal-box" onSubmit={submit}>
        <h2 className="modal-title">{isEdit ? `Ubah ${noun}` : `Tambah ${noun}`}</h2>
        <p className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          Kode Internal adalah kunci utama. {isEdit ? "Tidak dapat diubah." : "Diisi otomatis (sementara)."}
        </p>

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="item-form-kode-internal">Kode Internal</label>
            <input id="item-form-kode-internal" className="form-input mono" readOnly
              value={isEdit ? form.internalCode : ""}
              placeholder={isEdit ? undefined : "Dibuat otomatis dari kategori"} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="item-form-kode-pasar">Kode Pasar</label>
            <MarketCodeEditor id="item-form-kode-pasar" value={form.marketCodes}
              onChange={(arr) => set("marketCodes", arr)} />
          </div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="item-form-nama">{form.isSet ? "Nama (Jenis Set)" : "Nama (Jenis Barang)"}</label>
          <input id="item-form-nama" className="form-input" value={form.name} autoFocus
            onChange={(e) => set("name", e.target.value)} />
          {/* Disabled-control sweep (manual-testing report) — shared convention: a blank
              required text field states it, the same way Kategori's own "Wajib diisi." already
              does just below. */}
          {!form.name.trim() && <p className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</p>}
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="item-form-fitment">Tipe Mobil (Fitment)</label>
          <FitmentEditor id="item-form-fitment" value={form.fitment} onChange={(arr) => set("fitment", arr)}
            cars={cars} onCreateCar={onCreateCar} />
        </div>

        {isEdit ? (
          <div className="form-row">
            <label className="form-label" htmlFor="item-form-kategori-readonly">Kategori</label>
            <input id="item-form-kategori-readonly" className="form-input" readOnly value={form.category || "—"} />
            <p className="form-hint">Kategori tidak dapat diubah setelah barang dibuat.</p>
          </div>
        ) : (
          <div className="form-row">
            <label className="form-label" htmlFor="item-form-kategori">Kategori</label>
            <CategoryPicker
              id="item-form-kategori"
              value={form.category}
              categories={categories}
              onChange={(name) => {
                set("category", name || "");
                // Drop pending new-category if the user switches to an existing category or clears.
                setPendingCategory((pc) => (pc && pc.name === name ? pc : null));
              }}
              onNewCategory={(cat) => setPendingCategory(cat)}
            />
            {!form.category && <p className="form-hint">Wajib diisi.</p>}
          </div>
        )}

        <div className="form-row">
          <label className="form-label">Saudara (Substitusi)</label>
          <SiblingEditor
            value={form.siblingCodes}
            onChange={(arr) => set("siblingCodes", arr)}
            items={items}
            selfCode={isEdit ? form.internalCode : null}
            itemName={form.name}
            itemCategory={form.category}
          />
          <p className="form-hint">Barang saudara bisa dipakai sebagai pengganti saat stok habis.</p>
        </div>

        {isEdit && (
          <div className="form-row">
            <label className="form-label">Jenis Barang</label>
            <div className="seg">
              <button type="button" className={!form.isSet ? "active" : ""} onClick={() => setType(false)}>Spare part</button>
              <button type="button" className={form.isSet ? "active" : ""} onClick={() => setType(true)}>SET</button>
            </div>
            {typeError && <div className="form-hint" style={{ color: "var(--red)" }}>{typeError}</div>}
          </div>
        )}

        {isEdit ? (
          <div className="form-row">
            <label className="form-label">Stok</label>
            <div className="readonly-stock">
              Stok Pusat: <b>{form.stokPusat}</b> · Stok Cabang: <b>{form.stokCabang}</b>
            </div>
            <button type="button" className="btn btn-sm" style={{ marginTop: "var(--gap-sm)" }}
              onClick={() => onAdjust(initial)}>
              Sesuaikan Stok
            </button>
            <p className="form-hint">Perubahan stok dicatat di riwayat melalui Penyesuaian Stok.</p>
          </div>
        ) : (
          <div className="form-grid">
            <div className="form-row">
              <label className="form-label" htmlFor="item-form-stok-pusat">Stok Pusat</label>
              <input id="item-form-stok-pusat" className="form-input" type="number" value={form.stokPusat}
                onChange={(e) => set("stokPusat", e.target.value)} />
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="item-form-stok-cabang">Stok Cabang</label>
              <input id="item-form-stok-cabang" className="form-input" type="number" value={form.stokCabang}
                onChange={(e) => set("stokCabang", e.target.value)} />
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="item-form-asal-stok">Asal Stok (opsional)</label>
              <ContactPicker id="item-form-asal-stok" value={openingContactId} contacts={pickableContacts}
                onChange={setOpeningContactId} onCreateContact={onCreateContact} />
              <p className="form-hint">Kosongkan jika rakitan sendiri / tidak diketahui.</p>
            </div>
          </div>
        )}

        <div className="form-row">
          <label className="form-label" htmlFor="item-form-stok-minimum">Stok Minimum (kosong = pakai batas umum)</label>
          <input id="item-form-stok-minimum" className="form-input" type="number" min="0" value={form.minStock ?? ""}
            onChange={(e) => set("minStock", e.target.value)} />
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="item-form-keterangan">Keterangan</label>
          <textarea id="item-form-keterangan" className="form-input" value={form.notes}
            onChange={(e) => set("notes", e.target.value)} />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary"
            disabled={!form.name.trim() || (!isEdit && !form.category.trim())}>
            {isEdit ? "Simpan" : "Tambah"}
          </button>
        </div>
      </form>
    </div>
  );
}
